import React from 'react';
import { Box, Typography, alpha, Avatar, Paper } from '@mui/material';
import { MdDarkMode, MdLightMode, MdSettingsSuggest } from 'react-icons/md';
import { getOrgTokens } from '../../../theme/style/organizationPalette';
import { TableHeaderContainer } from '../../../components/container/TableHeaderContainer';
import { TableTitleContainer } from '../../../components/container/TableTitleContainer';

const GeneralTab = ({
    t,
    mode,
    user,
    username,
    primarySolid,
    setThemeMode,
    activeMode
}) => {
    const isDark = activeMode === 'dark';
    const tok = getOrgTokens(isDark);

    const themeOptions = [
        {
            value: 'light',
            label: 'Light Mode',
            description: 'Classic and bright interface',
            icon: <MdLightMode size={24} />,
            color: '#f59e0b',
        },
        {
            value: 'dark',
            label: 'Dark Mode',
            description: 'Modern and eye-friendly dark look',
            icon: <MdDarkMode size={24} />,
            color: '#a855f7',
        },
        {
            value: 'system',
            label: 'Use System',
            description: 'Matches your device settings automatically',
            icon: <MdSettingsSuggest size={24} />,
            color: '#34d399',
        }
    ];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: tok.spacing.pageGap }}>
            
            {/* Account Info Section Container */}
            <Paper variant="Table Container">
                <Box sx={{ 
                    ...tok.sectionHeading, 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1.5,
                    pl: 0,
                    position: 'relative',
                    p: tok.spacing.pageGap,
                    mb: 0
                }}>
                    <Box sx={{ 
                        width: tok.sectionHeading.barWidth, 
                        height: '24px', 
                        bgcolor: tok.sectionHeading.barColor, 
                        borderRadius: '2px' 
                    }} />
                    <Typography sx={{ 
                        fontFamily: tok.sectionHeading.fontFamily,
                        fontSize: tok.sectionHeading.fontSize,
                        fontWeight: tok.sectionHeading.fontWeight,
                        color: tok.sectionHeading.color
                    }}>
                        {t("Account Information")}
                    </Typography>
                </Box>
                <Box sx={{ p: tok.spacing.pageGap }}>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: tok.spacing.pageGap }}>
                        {[
                            { label: 'Username', value: username || "—" },
                            { label: 'Email Address', value: user?.email || "—" },
                            { label: 'User ID', value: user?.id || "—", fullWidth: true, isCode: true }
                        ].map((field, idx) => (
                            <Box key={idx} sx={{ gridColumn: field.fullWidth ? { xs: 'span 1', sm: 'span 2' } : 'auto' }}>
                                <Typography variant="caption" sx={{
                                    fontWeight: tok.font.weight.medium,
                                    color: tok.text.muted,
                                    mb: 0.5,
                                    display: 'block',
                                    letterSpacing: '0.4px',
                                    fontSize: '11px',
                                    fontFamily: tok.font.family,
                                    textTransform: 'none'
                                 }}>
                                    {t(field.label)}
                                </Typography>
                                <Box sx={{
                                    p: 1.5,
                                    borderRadius: tok.radius.card,
                                    bgcolor: tok.bg.tabContainer,
                                    border: '1px solid',
                                    borderColor: tok.border.card,
                                    fontWeight: tok.font.weight.medium,
                                    fontSize: '14px',
                                    color: tok.text.primary,
                                    fontFamily: field.isCode ? 'monospace' : tok.font.family,
                                    opacity: field.isCode ? 0.7 : 1
                                }}>
                                    {field.value}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Paper>

            {/* Appearance Section Container */}
            <Paper variant="Table Container">
                <Box sx={{ 
                    ...tok.sectionHeading, 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1.5,
                    pl: 0,
                    position: 'relative',
                    p: tok.spacing.pageGap,
                    mb: 0
                }}>
                    <Box sx={{ 
                        width: tok.sectionHeading.barWidth, 
                        height: '24px', 
                        bgcolor: tok.sectionHeading.barColor, 
                        borderRadius: '2px' 
                    }} />
                    <Typography sx={{ 
                        fontFamily: tok.sectionHeading.fontFamily,
                        fontSize: tok.sectionHeading.fontSize,
                        fontWeight: tok.sectionHeading.fontWeight,
                        color: tok.sectionHeading.color
                    }}>
                        {t("Interface Appearance")}
                    </Typography>
                </Box>
                <Box sx={{ p: tok.spacing.pageGap }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: tok.spacing.pageGap }}>
                        {themeOptions.map((option) => {
                            const isActive = mode === option.value;
                            return (
                                <Box
                                    key={option.value}
                                    onClick={() => setThemeMode(option.value)}
                                    sx={{
                                        p: 2.2,
                                        borderRadius: tok.radius.card,
                                        cursor: 'pointer',
                                        border: '1px solid',
                                        borderColor: isActive ? option.color : tok.border.card,
                                        bgcolor: isActive ? alpha(option.color, 0.08) : tok.bg.tabContainer,
                                        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                                        boxShadow: isActive ? `0 8px 24px ${alpha(option.color, 0.15)}` : 'none',
                                        '&:hover': {
                                            borderColor: option.color,
                                            transform: 'translateY(-2px)'
                                        }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                                        <Avatar sx={{
                                            bgcolor: alpha(option.color, 0.1),
                                            color: option.color,
                                            width: 32,
                                            height: 32,
                                        }}>
                                            {React.cloneElement(option.icon, { size: 16 })}
                                        </Avatar>
                                        <Typography sx={{ fontWeight: tok.font.weight.semibold, fontSize: '14px', fontFamily: tok.font.family, color: tok.text.primary }}>
                                            {t(option.label)}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{ color: tok.text.muted, fontSize: '12px', lineHeight: 1.4, fontWeight: tok.font.weight.medium, fontFamily: tok.font.family }}>
                                        {t(option.description)}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </Paper>

        </Box>
    );
};

export default GeneralTab;
